import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useRestaurants } from '@/hooks/useRestaurants';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@/components/ui';
import { BarcodeScanner } from '@/components/scanner/BarcodeScanner';
import { BatchScanModal } from '@/components/scanner/BatchScanModal';
import {
  DashboardHeader,
  StatsCards,
  TrendChart,
  DailySummary,
  ZonesGrid,
  LowStockWidget,
  RecentActivityWidget,
  QuickActionsGrid,
  MultiRestaurantSection,
  ScanResultModal,
  CriticalAlertsBanner,
  StockValueWidget,
} from '@/components/dashboard';
import { ExpiringLotsWidget } from '@/components/lots';
import type {
  DashboardStats,
  DailySummaryData,
  LowStockProduct,
  ChartDataPoint,
  SearchResult,
} from '@/components/dashboard';
import { openFoodFactsService, ProductLookupResult } from '@/services/openFoodFactsService';
import { toast } from '@/hooks/useToast';
import { stockService } from '@/services/stockService';
import { productService } from '@/services/productService';
import { movementService } from '@/services/movementService';
import { lotService } from '@/services/lotService';
import type { Mouvement } from '@/types/inventory.types';
import type { Product } from '@/types/product.types';
import { format, subDays, startOfDay, isToday, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { createLogger } from '@/lib/logger';

const log = createLogger('DashboardPage');

export function DashboardPage() {
  const { profile, currentRestaurantId, currentRestaurant } = useAuth();
  const { restaurants, hasMultipleRestaurants } = useRestaurants();
  const navigate = useNavigate();

  // Expiring lots count for alerts
  const [expiringLotsCount, setExpiringLotsCount] = useState(0);

  // Stock value
  const [stockValue, setStockValue] = useState(0);

  // Loading & Stats
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalProducts: 0,
    totalStocks: 0,
    criticalStocks: 0,
    warningStocks: 0,
    positiveZone: 0,
    negativeZone: 0,
    frigoZone: 0,
    reserveZone: 0,
    caveZone: 0,
    entretienZone: 0,
  });

  // Data
  const [lowStockProducts, setLowStockProducts] = useState<LowStockProduct[]>([]);
  const [recentMovements, setRecentMovements] = useState<Mouvement[]>([]);
  const [allMovements, setAllMovements] = useState<Mouvement[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);

  // Daily summary
  const [dailySummary, setDailySummary] = useState<DailySummaryData>({
    movementsToday: 0,
    entriesTotal: 0,
    exitsTotal: 0,
    productsUpdated: 0,
  });

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Scanner
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isBatchScanOpen, setIsBatchScanOpen] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [scanResult, setScanResult] = useState<{
    barcode: string;
    lookupResult: ProductLookupResult | null;
    existingProduct: { nom: string; categorie: string } | null;
  } | null>(null);

  // Handle barcode scan
  const handleBarcodeScan = async (barcode: string) => {
    setIsScannerOpen(false);
    setIsLookingUp(true);
    setScanResult({ barcode, lookupResult: null, existingProduct: null });

    try {
      if (currentRestaurantId) {
        const productsResult = await productService.getProducts(currentRestaurantId);
        const existingProduct = productsResult.data?.find(
          (p) => p.code_barre === barcode
        );

        if (existingProduct) {
          setScanResult({
            barcode,
            lookupResult: null,
            existingProduct: {
              nom: existingProduct.nom,
              categorie: existingProduct.categorie,
            },
          });
          setIsLookingUp(false);
          return;
        }
      }

      const lookupResult = await openFoodFactsService.lookupBarcode(barcode);
      setScanResult({ barcode, lookupResult, existingProduct: null });
    } catch (error) {
      log.error('Error during barcode lookup', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de rechercher le produit',
        variant: 'destructive',
      });
      setScanResult(null);
    } finally {
      setIsLookingUp(false);
    }
  };

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    if (!currentRestaurantId) return;

    setIsLoading(true);

    try {
      const [
        productsResult,
        stocksResult,
        criticalResult,
        warningResult,
        movementsResult,
        allMovementsResult,
        expiringLotsResult,
      ] = await Promise.all([
        productService.getProducts(currentRestaurantId),
        stockService.getStocks(currentRestaurantId),
        stockService.getCriticalStocks(currentRestaurantId),
        stockService.getWarningStocks(currentRestaurantId),
        movementService.getMovements(currentRestaurantId, { limit: 5 }),
        movementService.getMovements(currentRestaurantId, { limit: 100 }),
        lotService.getExpiringLots(currentRestaurantId, 7),
      ]);

      const stocks = stocksResult.data || [];
      const criticalStocks = criticalResult.data || [];
      const warningStocks = warningResult.data || [];
      const expiringLots = expiringLotsResult || [];

      setExpiringLotsCount(expiringLots.length);

      // Calculate stock totals
      const stockTotalByProduct: Record<string, number> = {};
      stocks.forEach((stock) => {
        stockTotalByProduct[stock.produit_id] =
          (stockTotalByProduct[stock.produit_id] || 0) + stock.quantite;
      });

      // Calculate total stock value
      const products = productsResult.data || [];
      const totalStockValue = products.reduce((total, product) => {
        const stockQty = stockTotalByProduct[product.id] || 0;
        const price = product.cout_unitaire || 0;
        return total + (stockQty * price);
      }, 0);
      setStockValue(totalStockValue);

      // Combine low stock products
      const lowStocks: LowStockProduct[] = [
        ...criticalStocks.map((s) => ({
          product: s,
          level: 'critical' as const,
          stockTotal: stockTotalByProduct[s.produit_id] || s.quantite,
        })),
        ...warningStocks.map((s) => ({
          product: s,
          level: 'warning' as const,
          stockTotal: stockTotalByProduct[s.produit_id] || s.quantite,
        })),
      ];

      lowStocks.sort((a, b) => {
        if (a.level !== b.level) return a.level === 'critical' ? -1 : 1;
        return a.stockTotal - b.stockTotal;
      });

      setLowStockProducts(lowStocks.slice(0, 5));
      setRecentMovements(movementsResult.data || []);
      setAllMovements(allMovementsResult.data || []);
      setAllProducts(productsResult.data || []);

      // Calculate daily summary
      const todayMovements = (allMovementsResult.data || []).filter((m) =>
        isToday(parseISO(m.date))
      );

      setDailySummary({
        movementsToday: todayMovements.length,
        entriesTotal: todayMovements
          .filter((m) => m.type === 'entrée')
          .reduce((sum, m) => sum + m.quantite, 0),
        exitsTotal: todayMovements
          .filter((m) => m.type === 'sortie')
          .reduce((sum, m) => sum + m.quantite, 0),
        productsUpdated: new Set(todayMovements.map((m) => m.produit_id)).size,
      });

      setStats({
        totalProducts: productsResult.data?.length || 0,
        totalStocks: stocks.length,
        criticalStocks: criticalStocks.length,
        warningStocks: warningStocks.length,
        positiveZone: stocks.filter((s) => s.zone === 'positive').length,
        negativeZone: stocks.filter((s) => s.zone === 'negative').length,
        frigoZone: stocks.filter((s) => s.zone === 'frigo').length,
        reserveZone: stocks.filter((s) => s.zone === 'reserve').length,
        caveZone: stocks.filter((s) => s.zone === 'cave').length,
        entretienZone: stocks.filter((s) => s.zone === 'entretien').length,
      });
    } catch (error) {
      log.error('Error loading dashboard data', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les données du tableau de bord',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentRestaurantId]);

  useEffect(() => {
    if (currentRestaurantId) {
      loadDashboardData();
    }
  }, [currentRestaurantId, loadDashboardData]);

  // Chart data (last 7 days)
  const chartData = useMemo<ChartDataPoint[]>(() => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = subDays(new Date(), 6 - i);
      return {
        date: startOfDay(date).toISOString(),
        label: format(date, 'EEE', { locale: fr }),
        fullDate: format(date, 'd MMM', { locale: fr }),
        entries: 0,
        exits: 0,
      };
    });

    allMovements.forEach((movement) => {
      const movementDate = startOfDay(parseISO(movement.date)).toISOString();
      const dayData = last7Days.find((d) => d.date === movementDate);
      if (dayData) {
        if (movement.type === 'entrée') {
          dayData.entries += movement.quantite;
        } else if (movement.type === 'sortie') {
          dayData.exits += movement.quantite;
        }
      }
    });

    return last7Days;
  }, [allMovements]);

  // Search results
  const searchResults = useMemo<SearchResult[]>(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) return [];

    const query = searchQuery.toLowerCase();
    const results: SearchResult[] = [];

    allProducts.forEach((product) => {
      if (
        product.nom.toLowerCase().includes(query) ||
        product.categorie.toLowerCase().includes(query) ||
        product.code_barre?.toLowerCase().includes(query)
      ) {
        results.push({
          type: 'product',
          id: product.id,
          name: product.nom,
          subtitle: product.categorie,
          icon: 'package',
        });
      }
    });

    recentMovements.forEach((movement) => {
      if (movement.produits?.nom.toLowerCase().includes(query)) {
        results.push({
          type: 'movement',
          id: movement.id,
          name: movement.produits.nom,
          subtitle: `${movement.type} - ${movement.quantite} ${movement.unite}`,
          icon: 'arrow',
        });
      }
    });

    return results.slice(0, 6);
  }, [searchQuery, allProducts, recentMovements]);

  // Logout handler
  const handleLogout = async () => {
    try {
      const authService = await import('@/services/authService');
      const result = await authService.authService.logout();

      if (result.success) {
        toast({ title: 'Déconnexion réussie', description: 'À bientôt !' });
        navigate('/login');
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-8">
        <div className="mx-auto max-w-7xl">
          <Spinner centered label="Chargement du tableau de bord..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-8">
      {/* Barcode Scanner */}
      <BarcodeScanner
        isOpen={isScannerOpen}
        onScan={handleBarcodeScan}
        onClose={() => setIsScannerOpen(false)}
      />

      {/* Batch Scan Modal */}
      <BatchScanModal
        isOpen={isBatchScanOpen}
        onClose={() => setIsBatchScanOpen(false)}
      />

      {/* Scan Result Modal */}
      <ScanResultModal
        isLookingUp={isLookingUp}
        scanResult={scanResult}
        onClose={() => setScanResult(null)}
        onOpenScanner={() => setIsScannerOpen(true)}
      />

      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <DashboardHeader
          profile={profile}
          restaurantName={currentRestaurant?.nom}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          isSearchFocused={isSearchFocused}
          onSearchFocus={() => setIsSearchFocused(true)}
          onSearchBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
          searchResults={searchResults}
          onLogout={handleLogout}
        />

        {/* Critical Alerts Banner */}
        <CriticalAlertsBanner
          criticalStocks={stats.criticalStocks}
          expiringLots={expiringLotsCount}
        />

        {/* Stats Cards */}
        <StatsCards stats={stats} />

        {/* Trend Chart + Daily Summary + Stock Value */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="lg:col-span-2">
            <TrendChart data={chartData} />
          </div>
          <DailySummary summary={dailySummary} />
          <StockValueWidget totalValue={stockValue} />
        </div>

        {/* Zones Grid */}
        <ZonesGrid stats={stats} />

        {/* Widgets */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <LowStockWidget products={lowStockProducts} />
          {currentRestaurantId && (
            <ExpiringLotsWidget
              restaurantId={currentRestaurantId}
              daysThreshold={7}
              maxItems={5}
              onViewAll={() => navigate('/lots')}
              onLotClick={(lotId) => navigate(`/lots/${lotId}`)}
            />
          )}
          <RecentActivityWidget movements={recentMovements} />
        </div>

        {/* Multi-Restaurant Section */}
        {hasMultipleRestaurants && <MultiRestaurantSection restaurants={restaurants} />}

        {/* Quick Actions */}
        <QuickActionsGrid stats={stats} onOpenScanner={() => setIsScannerOpen(true)} onOpenBatchScan={() => setIsBatchScanOpen(true)} />
      </div>
    </div>
  );
}
